<template>
	<div class="surf-tabs-wrapper">
		<SimpleHScroll v-if="useMobLayout">
			<div class="rs-o-surf-container">
				<div class="surf-tabs" :class="composeClass">
					<div
						v-for="(item, index) in tabs"
						:key="index"
						class="surf-tabs-item"
						:class="{ active: activeIndex === index }"
						@click="handleTabClick(index, item)"
					>
						{{ getTabLabel(item) }}
					</div>
				</div>
			</div>
		</SimpleHScroll>
		<div v-else class="surf-tabs" :class="composeClass">
			<div
				v-for="(item, index) in tabs"
				:key="index"
				class="surf-tabs-item"
				:class="{ active: activeIndex === index }"
				@click="handleTabClick(index, item)"
			>
				{{ getTabLabel(item) }}
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { SimpleHScroll } from '@rebuild/components/scroll';

const props = defineProps({
	// 标签列表，支持字符串数组或对象数组
	tabs: {
		type: Array,
		required: true,
		default: () => []
	},
	// 默认选中的索引
	defaultActiveIndex: {
		type: Number,
		default: 0
	},
	// 如果 tabs 是对象数组，用于获取显示文本的字段名
	labelKey: {
		type: String,
		default: 'label'
	},
	// 样式风格：'text' 文本风格，'button' 按钮风格
	variant: {
		type: String,
		default: 'text',
		validator: value => ['text', 'button'].includes(value)
	},
	useMobLayout: {
		type: Boolean,
		default: false
	}
});

const emit = defineEmits(['change']);

const composeClass = computed(() => {
	return {
		'surf-tabs-text': props.variant === 'text',
		'surf-tabs-button': props.variant === 'button',
		'surf-tabs--mobile': props.useMobLayout
	};
});

const activeIndex = ref(props.defaultActiveIndex);

// 获取标签显示文本
const getTabLabel = item => {
	if (typeof item === 'string') {
		return item;
	}
	return item?.[props.labelKey] || item?.title || item?.name || '';
};

// 处理标签点击
const handleTabClick = (index, item) => {
	if (activeIndex.value === index) return;
	activeIndex.value = index;
	emit('change', index, item);
};

// 暴露方法给父组件
defineExpose({
	activeIndex: () => activeIndex.value,
	setActiveIndex: index => {
		if (index >= 0 && index < props.tabs.length) {
			activeIndex.value = index;
		}
	}
});
</script>

<style lang="less" scoped>
.surf-tabs {
	display: flex;
	align-items: baseline;
	gap: 20px;
	flex-wrap: wrap;

	.surf-tabs-item {
		cursor: pointer;
		user-select: none;
		white-space: nowrap;
	}

	// 文本风格（默认）
	&.surf-tabs-text .surf-tabs-item {
		color: rgba(var(--surf-inverse-color-rgb), 0.4);
		font-size: 20px;
		font-weight: 400;
		line-height: 28px; /* 140% */
		transition: color 0.3s ease;

		&:hover {
			color: rgba(var(--surf-inverse-color-rgb), 0.8);
		}

		&.active {
			color: var(--surf-inverse-color);
			font-size: 24px;
			font-weight: 600;
			line-height: 32px; /* 133.333% */
		}
	}

	// 按钮风格
	&.surf-tabs-button {
		align-items: center;
		gap: 12px;

		.surf-tabs-item {
			position: relative;
			height: 28px;
			line-height: 28px;
			padding: 0 12px;
			border-radius: 4px;
			color: var(--surf-inverse-color);
			font-size: 14px;
			font-weight: 400;
			background: transparent;
			transition: all 0.3s ease;

			&::before {
				content: '';
				position: absolute;
				top: 0;
				left: 0;
				width: 200%;
				height: 200%;
				border-radius: 8px;
				border: 1px solid var(--surf-inverse-color);
				transform: scale(0.5);
				transform-origin: 0 0;
				pointer-events: none;
			}

			&:hover {
				background: rgba(var(--surf-inverse-color-rgb), 0.1);
			}

			&.active {
				background: var(--surf-inverse-color);
				color: var(--theme-color);
				font-weight: 500;

				&::before {
					display: none;
				}
			}
		}
	}
}

.surf-tabs--mobile.surf-tabs {
	flex-wrap: nowrap;

	// 文本风格适配
	&.surf-tabs-text {
		align-items: center; // 从基线对齐调整为垂直居中对齐
		gap: 20px; // 每个Tab的间距 20

		.surf-tabs-item {
			font-size: 14px; // 默认态字号 14
			font-weight: 400; // 字重 400
			line-height: 18px;

			&.active {
				font-size: 16px; // 选中态字号 16
				font-weight: 600; // 字重 600
				line-height: 24px;
			}
		}
	}

	// 按钮风格适配
	&.surf-tabs-button {
		gap: 20px; // 每个Tab的间距 20
	}
}
</style>
